import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';

interface UseSessionManagerOptions {
  inactivityTimeout?: number; // in milliseconds
  otpExpirationTime?: number; // in milliseconds
  onSessionExpired?: () => void;
  onOtpExpired?: () => void;
}

export function useSessionManager({
  inactivityTimeout = 60 * 60 * 1000, // 1 hour default
  otpExpirationTime = 2 * 60 * 60 * 1000, // 2 hours default
  onSessionExpired,
  onOtpExpired
}: UseSessionManagerOptions = {}) {
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const otpTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(async () => {
      try {
        await signOut(auth);
        localStorage.removeItem('isOtpVerified');
        localStorage.removeItem('otpVerifiedAt');
        onSessionExpired?.();
      } catch (error) {
        console.error('Error during automatic logout:', error);
      }
    }, inactivityTimeout);
  }, [inactivityTimeout, onSessionExpired]);

  // Check OTP expiration
  const checkOtpExpiration = useCallback(() => {
    const otpVerifiedAt = localStorage.getItem('otpVerifiedAt');
    const isOtpVerified = localStorage.getItem('isOtpVerified') === 'true';
    
    if (isOtpVerified && otpVerifiedAt) {
      const verifiedTime = parseInt(otpVerifiedAt, 10);
      const currentTime = Date.now();
      
      if (currentTime - verifiedTime > otpExpirationTime) {
        localStorage.removeItem('isOtpVerified');
        localStorage.removeItem('otpVerifiedAt');
        onOtpExpired?.();
        return false;
      }
    }
    
    return isOtpVerified;
  }, [otpExpirationTime, onOtpExpired]);

  // Set OTP verification with timestamp
  const setOtpVerified = useCallback(() => {
    localStorage.setItem('isOtpVerified', 'true');
    localStorage.setItem('otpVerifiedAt', Date.now().toString());
  }, []);

  // Activity event handlers
  const handleActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start inactivity timer
    resetInactivityTimer();

    // Check OTP expiration on mount and set up periodic check
    checkOtpExpiration();
    const otpCheckInterval = setInterval(checkOtpExpiration, 60000); // Check every minute

    return () => {
      // Cleanup event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });

      // Cleanup timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (otpTimerRef.current) {
        clearTimeout(otpTimerRef.current);
      }
      clearInterval(otpCheckInterval);
    };
  }, [handleActivity, resetInactivityTimer, checkOtpExpiration]);

  return {
    resetInactivityTimer,
    checkOtpExpiration,
    setOtpVerified,
    getLastActivity: () => lastActivityRef.current
  };
}